import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, CircularProgress, Box, Button } from '@mui/material';
import { format, parse, addMinutes } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '@mui/material/styles';
import { listStorefrontBusySlots } from '../../api/storefront/storefront';

const TimeSlotSelector = ({ 
  selectedDate, 
  companyHours, 
  onTimeSelect, 
  companyId, 
  selectedTime,
  selectedService,
  excludeAppointmentId,
}) => {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const { companyData } = useAuth();

  // Get user's local timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Get company timezone from browser if not set
  const companyTimezone = companyData?.timezone || userTimezone;

  console.log('Timezone Information:', {
    userTimezone,
    companyTimezone,
    offset: new Date().getTimezoneOffset()
  });

  const formatTimeForDisplay = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    }).format(date);
  };

  const calculateTotalDuration = (service, baseDuration) => {
    if (!service) return baseDuration;

    let totalDuration = service.duration || baseDuration;

    // Add duration from selected addons
    if (service.selectedAddons?.length > 0) {
      totalDuration += service.selectedAddons.reduce((sum, addon) => {
        return sum + (addon.duration || 0);
      }, 0);
    }

    // Add duration from selected package
    if (service.selectedPackage?.duration) {
      totalDuration += service.selectedPackage.duration;
    }

    return totalDuration;
  };

  const getServiceSettings = useCallback(() => {
    const defaultSettings = {
      duration: selectedService?.duration || 30,
      bufferTime: 15,
      timeSlotInterval: 15,
      allowOverlapping: false
    };

    return {
      ...defaultSettings,
      duration: calculateTotalDuration(selectedService, defaultSettings.duration)
    };
  }, [selectedService]);

  const checkSlotAvailability = useCallback(async (slots) => {
    if (!slots.length || !companyId) return slots;

    try {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      const slotsForDay = await listStorefrontBusySlots({
        companyId,
        from: startOfDay,
        to: endOfDay,
      });
      const activeAppointments = (slotsForDay || [])
        .filter((slot) => String(slot.id || '') !== String(excludeAppointmentId || ''))
        .map((slot) => ({
          id: slot.id,
          dateTime: new Date(slot.startTime),
          endTime: new Date(slot.endTime),
        }))
        .filter((apt) => !Number.isNaN(apt.dateTime.getTime()) && !Number.isNaN(apt.endTime.getTime()));

      const settings = getServiceSettings();
      const slotDuration = settings.duration + (settings.bufferTime || 0);

      // Check each slot for availability
      return slots.map(slot => {
        const slotStart = new Date(slot);
        const slotEnd = addMinutes(slotStart, slotDuration);

        // Check for any overlap with existing appointments
        const isOverlapping = activeAppointments.some(apt => {
          // Convert both times to timestamps for accurate comparison
          const aptStart = apt.dateTime.getTime();
          const aptEnd = apt.endTime.getTime();
          const newSlotStart = slotStart.getTime();
          const newSlotEnd = slotEnd.getTime();

          // Check for any overlap scenarios
          return (
            (newSlotStart >= aptStart && newSlotStart < aptEnd) || // Start time falls within existing appointment
            (newSlotEnd > aptStart && newSlotEnd <= aptEnd) || // End time falls within existing appointment
            (newSlotStart <= aptStart && newSlotEnd >= aptEnd) // Completely encompasses existing appointment
          );
        });

        return {
          dateTime: slotStart,
          displayTime: formatTimeForDisplay(slotStart),
          isAvailable: !isOverlapping,
          duration: slotDuration
        };
      });
    } catch (error) {
      console.error('Error checking availability:', error);
      return slots.map(slot => ({
        dateTime: new Date(slot),
        displayTime: formatTimeForDisplay(new Date(slot)),
        isAvailable: false // Default to unavailable on error
      }));
    }
  }, [selectedDate, companyId, getServiceSettings, excludeAppointmentId]);

  const fetchAvailableSlots = useCallback(async () => {
    if (!selectedDate || !companyHours || !selectedService) {
      setAvailableSlots([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
      const dayHours = companyHours[dayOfWeek];

      if (!dayHours?.isOpen) {
        setError(`We're closed on ${format(selectedDate, 'EEEE')}s`);
        setAvailableSlots([]);
        return;
      }

      const settings = getServiceSettings();
      const openTime = parse(dayHours.open.toLowerCase(), 'h:mm aa', selectedDate);
      const closeTime = parse(dayHours.close.toLowerCase(), 'h:mm aa', selectedDate);

      let breakStart = null;
      let breakEnd = null;
      if (dayHours.hasBreak && dayHours.breakStart && dayHours.breakEnd) {
        breakStart = parse(dayHours.breakStart.toLowerCase(), 'h:mm aa', selectedDate);
        breakEnd = parse(dayHours.breakEnd.toLowerCase(), 'h:mm aa', selectedDate);
      }

      const slots = [];
      let currentTime = new Date(selectedDate);
      currentTime.setHours(openTime.getHours(), openTime.getMinutes(), 0);

      const generateSlotsForPeriod = (startTime, endTime) => {
        let current = new Date(startTime);
        while (current < endTime) {
          if (current > new Date()) { // Only future slots
            slots.push(new Date(current));
          }
          current = addMinutes(current, settings.timeSlotInterval);
        }
      };

      if (breakStart && breakEnd) {
        generateSlotsForPeriod(currentTime, breakStart);
        currentTime = new Date(breakEnd);
        generateSlotsForPeriod(currentTime, closeTime);
      } else {
        generateSlotsForPeriod(currentTime, closeTime);
      }

      const availableSlots = await checkSlotAvailability(slots);
      setAvailableSlots(availableSlots.filter(slot => slot.isAvailable));
      setError('');
    } catch (err) {
      console.error('Error generating time slots:', err);
      setError('Error generating available time slots');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, companyHours, selectedService, checkSlotAvailability, getServiceSettings]);

  useEffect(() => {
    fetchAvailableSlots();
  }, [fetchAvailableSlots]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
        <Typography ml={2}>Loading available time slots...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" p={2}>
        {error}
      </Typography>
    );
  }

  if (availableSlots.length === 0) {
    return (
      <Typography color="text.secondary" align="center" p={2}>
        No time slots available for this date
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
        All times shown in your local timezone ({userTimezone})
      </Typography>
      <Grid container spacing={2}>
        {availableSlots.map((slot) => (
          <Grid item xs={6} sm={4} md={3} key={slot.dateTime.toISOString()}>
            <Button
              fullWidth
              variant={selectedTime?.getTime() === slot.dateTime.getTime() ? "contained" : "outlined"}
              onClick={() => {
                const dateTime = new Date(slot.dateTime);
                if (dateTime instanceof Date && !isNaN(dateTime)) {
                  onTimeSelect({
                    dateTime: dateTime,
                    displayTime: slot.displayTime
                  });
                } else {
                  console.error('Invalid date/time in slot:', slot);
                }
              }}
              sx={{
                py: 1.5,
                borderRadius: 2,
                backgroundColor: selectedTime?.getTime() === slot.dateTime.getTime() 
                  ? theme.palette.primary.main 
                  : 'transparent',
                color: selectedTime?.getTime() === slot.dateTime.getTime() 
                  ? 'white' 
                  : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: selectedTime?.getTime() === slot.dateTime.getTime() 
                    ? theme.palette.primary.dark 
                    : theme.palette.action.hover
                }
              }}
            >
              <Typography variant="body2">
                {slot.displayTime}
              </Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TimeSlotSelector;